// Android PWA back-button trap.
//
// In a `display: standalone` install on Android the back gesture pops
// the history stack — once the user is at the bottom of it the next
// back press closes the PWA, which is jarring for an app you live in.
//
// Strategy:
//   1. On install, seed a buffer of N sentinel ("trap") history entries
//      so even if a popstate listener somewhere else in the app (React
//      Router, replicache, etc.) interferes with one of them, the next
//      back press still lands on another trap rather than punching out.
//   2. After every popstate, on a microtask (so any other listeners on
//      the same event have already run), check the top of the stack
//      and re-push a trap if it isn't one.  Using a microtask, not the
//      synchronous handler, is the key: it lets us "win the race" with
//      anything that might replaceState in response to the same event.
//
// Installs only when actually running as an installed PWA — a normal
// browser tab keeps standard back semantics so users can still navigate
// out to where they came from.
//
// Reported by @sosemorris (bug report 2026-05-18: "Disable back button"),
// then re-reported when rapid taps still closed the app — that second
// pass is why we now use a microtask + multi-entry buffer.

const TRAP_KEY = "__pwa_back_trap__";
// Buffer size.  One trap is usually enough, but on Android Chrome a
// burst of back taps can occasionally race the popstate→pushState
// turnaround; padding a couple of extras gives us breathing room.
const BUFFER = 3;

let installed = false;

function isStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari home-screen exposes this non-standard flag.
  const navAny = navigator as Navigator & { standalone?: boolean };
  return navAny.standalone === true;
}

function hasTrapMarker(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as Record<string, unknown>)[TRAP_KEY] === true
  );
}

function pushTrap(): void {
  window.history.pushState({ [TRAP_KEY]: true }, "");
}

export function installBackTrap(): void {
  if (installed) return;
  if (!isStandalonePwa()) return;
  installed = true;

  // Seed the buffer.
  for (let i = 0; i < BUFFER; i++) pushTrap();

  window.addEventListener("popstate", () => {
    // Defer so we run after any other popstate listeners (notably
    // React Router's history sync) have done their thing.  If the top
    // of the stack ended up as a non-trap entry, re-push so the next
    // back press still hits a trap.
    queueMicrotask(() => {
      if (!hasTrapMarker(window.history.state)) {
        pushTrap();
      }
    });
  });
}
